import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, UserPlus, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";

const STATUS_BADGES = {
  active: "bg-emerald-100 text-emerald-800",
  pending: "bg-yellow-100 text-yellow-800",
  rejected: "bg-red-100 text-red-700",
};
const STATUS_LABELS = { active: "Aktif", pending: "Menunggu", rejected: "Ditolak" };

const emptyForm = { name: "", email: "", password: "", role: "operator" };

export default function Pengguna() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.role === "admin") fetchUsers();
  }, [currentUser, fetchUsers]);

  if (currentUser?.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="pengguna-forbidden">
        <ShieldAlert className="h-10 w-10 text-slate-300 mb-3" />
        <p className="text-slate-500">Halaman ini hanya dapat diakses oleh admin.</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || (!editingId && (!form.email || !form.password))) {
      toast.error("Lengkapi data pengguna");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("Pengguna diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("Pengguna ditambahkan");
      }
      setEditingId(null);
      setForm(emptyForm);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (u) => {
    setEditingId(u.user_id);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (u) => {
    if (confirmDelete !== u.user_id) {
      setConfirmDelete(u.user_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("Pengguna dihapus");
      setConfirmDelete(null);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleSetStatus = async (u, status) => {
    try {
      await api.put(`/users/${u.user_id}`, { status });
      toast.success(status === "active" ? `Akun ${u.name} disetujui` : `Akun ${u.name} ditolak`);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="pengguna-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">Manajemen Pengguna</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola akun admin dan operator</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 h-fit">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-800" />
              {editingId ? "Ubah Pengguna" : "Tambah Pengguna"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
              <div className="space-y-2">
                <Label>Nama</Label>
                <Input
                  data-testid="user-name-input"
                  placeholder="Nama lengkap"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="user-email-input"
                  placeholder="nama@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editingId}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Kata Sandi {editingId && <span className="text-slate-400 font-normal">(kosongkan jika tidak diubah)</span>}</Label>
                <Input
                  type="password"
                  data-testid="user-password-input"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required={!editingId}
                />
              </div>
              <div className="space-y-2">
                <Label>Peran</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pengurus" data-testid="user-role-pengurus">Pengurus</SelectItem>
                    <SelectItem value="operator" data-testid="user-role-operator">Operator</SelectItem>
                    <SelectItem value="koordinator_layanan" data-testid="user-role-koordinator">Koordinator Layanan</SelectItem>
                    <SelectItem value="driver" data-testid="user-role-driver">Driver</SelectItem>
                    <SelectItem value="admin" data-testid="user-role-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  data-testid="user-submit-button"
                  disabled={saving}
                  className="flex-1 bg-emerald-800 hover:bg-emerald-700 text-white"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Simpan Perubahan" : "Tambah Pengguna"}
                </Button>
                {editingId && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="user-cancel-edit-button"
                    onClick={() => { setEditingId(null); setForm(emptyForm); }}
                  >
                    Batal
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Daftar Pengguna ({users.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nama</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Peran</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-slate-400">
                      <Loader2 className="h-5 w-5 animate-spin inline-block" />
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                      <TableCell className="text-slate-500">{u.email}</TableCell>
                      <TableCell>
                        <span className="inline-block rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800">
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGES[u.status || "active"]}`} data-testid={`user-status-${u.user_id}`}>
                          {STATUS_LABELS[u.status || "active"]}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {(u.status || "active") === "pending" && (
                            <>
                              <button
                                data-testid={`user-approve-${u.user_id}`}
                                onClick={() => handleSetStatus(u, "active")}
                                title="Setujui akun"
                                className="rounded-md p-1.5 text-emerald-600 transition-colors duration-200 hover:bg-emerald-50"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </button>
                              <button
                                data-testid={`user-reject-${u.user_id}`}
                                onClick={() => handleSetStatus(u, "rejected")}
                                title="Tolak akun"
                                className="rounded-md p-1.5 text-red-500 transition-colors duration-200 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button
                            data-testid={`user-edit-${u.user_id}`}
                            onClick={() => handleEdit(u)}
                            className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {u.user_id !== currentUser?.user_id && (
                            <button
                              data-testid={`user-delete-${u.user_id}`}
                              onClick={() => handleDelete(u)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${
                                confirmDelete === u.user_id
                                  ? "bg-red-600 text-white"
                                  : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                              }`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
